import React, { useEffect, useState } from 'react';
import { Card, Space, Table, Tag, Input, Tooltip, Switch, Button, Popconfirm, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useMutation } from 'react-apollo';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import { Link } from 'react-router-dom';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { createEnhancedSearchFilter } from '../../../../core/utils/enhancedSearch';
import ARCHIVE_CAMPAIGN_ORDER from '../../../../core/GraphQl/Mutations/ARCHIVE_CAMPAIGN_ORDER';

const { Search } = Input;

export const CampaignOrderQueueReady = () => {
  const { timezone } = useTimezone();
  const {
    campaignOrders,
    loadingCampaignOrdersQueue,
    fetchCampaignOrdersQueue,
    pagination,
    setPagination,
  } = useCampaignOrderQueueContext();

  const [filterMediaPlanStrategy, setFilterMediaPlanStrategy] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [archivingIds, setArchivingIds] = useState(new Set());

  // MUTATIONS
  const [archiveCampaignOrder] = useMutation(ARCHIVE_CAMPAIGN_ORDER);

  useEffect(() => {
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchCampaignOrdersQueue = ({
    skip,
    first,
    name = '',
    sortField,
    sortOrder,
    hasMediaPlanStrategy = false,
  }) => {
    const userData = localStorage.getItem('userData');
    const orgId = userData ? JSON.parse(userData).role.org.id : null;
    const enhancedSearchFilter = createEnhancedSearchFilter(name);
    const whereConditions = [
      {
        status_in: ['APPROVED', 'LIVE_APPROVED', 'PAUSED_APPROVED'],
      },
    ];

    // Only add org filter if userData exists
    if (orgId) {
      whereConditions[0].orgs_some = {
        OR: [{ id: orgId }, { parentOrg_some: { id: orgId } }],
      };
    }

    if (enhancedSearchFilter) {
      whereConditions.push(enhancedSearchFilter);
    }

    // Filter by Has Media Plan Strategy
    if (hasMediaPlanStrategy) {
      whereConditions.push({
        campaigns_some: {
          hasMediaPlanStrategy: true,
        },
      });
    }

    fetchCampaignOrdersQueue({
      variables: {
        where: {
          AND: whereConditions,
        },
        orderBy:
          sortField && sortOrder ? `${sortField}_${sortOrder.toUpperCase()}` : 'updatedAt_DESC',
        skip,
        first,
      },
    });
  };

  const handleTableChange = ({ current, pageSize }, filters, sorter) => {
    const sortField = sorter.field;
    const sortOrder =
      sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;

    setPagination((prev) => ({ ...prev, current, pageSize }));
    handleFetchCampaignOrdersQueue({
      skip: (current - 1) * pageSize || 0,
      first: pageSize,
      sortField,
      sortOrder,
      name: searchValue,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  const handleSearchChange = (name) => {
    setSearchValue(name);
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
      name,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  const handleMediaPlanStrategyFilterChange = (checked) => {
    setFilterMediaPlanStrategy(checked);
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
      name: searchValue,
      hasMediaPlanStrategy: checked,
    });
  };

  const handleArchiveCampaignOrder = async (record) => {
    setArchivingIds((prev) => {
      const next = new Set(prev);
      next.add(record.id);
      return next;
    });

    try {
      await archiveCampaignOrder({
        variables: {
          id: record.id,
        },
      });

      message.success('Campaign order archived successfully');

      handleFetchCampaignOrdersQueue({
        skip: (pagination.current - 1) * pagination.pageSize,
        first: pagination.pageSize,
        name: searchValue,
        hasMediaPlanStrategy: filterMediaPlanStrategy,
      });
    } catch (error) {
      message.error('Failed to archive campaign order: ' + error.message);
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  const columns = [
    {
      title: 'Campaign Details',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (_, record) => (
        <Space size={4} direction="vertical">
          <Link to={`/campaigns/v2/orderqueue/details/${record.id}`}>
            {formatCampaignName(record, timezone)}
          </Link>
          <Space size={4}>
            <Tag color="blue">{(record.advertiser && record.advertiser.name) || ''}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Review Type',
      dataIndex: 'status',
      key: 'reviewType',
      render: (status) => {
        if (status === 'APPROVED') {
          return (
            <Tooltip title="Approved campaign order ready to launch.">
              <Tag color="green">Launch</Tag>
            </Tooltip>
          );
        }
        if (status === 'LIVE_APPROVED' || status === 'PAUSED_APPROVED') {
          return (
            <Tooltip title="Approved updates ready to be applied to the campaign.">
              <Tag color="gold">Update</Tag>
            </Tooltip>
          );
        }
        return <Tag>—</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        if (record.dspConfig && record.dspConfig.status) {
          switch (record.dspConfig.status) {
            case 'LAUNCHING':
              return <Tag color="orange">Launching</Tag>;
            case 'ERROR':
              return (
                <Tooltip title={record.dspConfig.error}>
                  <Tag color="red">Error</Tag>
                </Tooltip>
              );

            default:
              break;
          }
        }
        switch (text) {
          case 'APPROVED':
            return 'Pending Launch';

          case 'LIVE_APPROVED':
          case 'PAUSED_APPROVED':
            return 'Pending Update';

          default:
            break;
        }
      },
    },
    {
      title: 'Media Plan',
      key: 'hasMediaPlanStrategy',
      render: (_, record) => {
        const hasStrategy = record.campaigns?.some(campaign => campaign.hasMediaPlanStrategy);
        return hasStrategy ? (
          <Tag color="purple">Strategy</Tag>
        ) : null;
      },
    },
    {
      title: 'Launch Date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      render: (text) => moment(text).format('MM/DD/YYYY'),
    },
    {
      title: 'Submitted On',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      sorter: true,
      render: (text) => moment(text).format('MM/DD/YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to archive this campaign order?"
          description="This action cannot be undone. The campaign order will be moved to archived status."
          onConfirm={() => handleArchiveCampaignOrder(record)}
          okText="Yes, Archive"
          cancelText="Cancel"
          okType="danger"
        >
          <Button
            type="text"
            size="small"
            icon={<InboxOutlined />}
            loading={archivingIds.has(record.id)}
            danger
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title="READY TO LAUNCH"
      extra={
        <Space size="middle">
          <Space size="small">
            <Switch
              checked={filterMediaPlanStrategy}
              onChange={handleMediaPlanStrategyFilterChange}
              size="small"
            />
            <span style={{ fontSize: '12px' }}>Has Media Plan Strategy</span>
          </Space>
          <Search
            placeholder="Search campaign"
            style={{
              width: 200,
            }}
            onSearch={(value) => handleSearchChange(value)}
            enterButton
            allowClear
          />
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={campaignOrders}
        loading={loadingCampaignOrdersQueue}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </Card>
  );
};
